import * as React from "react";
import { useAction } from "convex/react";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  CreditCard,
  Loader2,
  ShieldCheck,
  Split,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";

const COLLECTION_TIME_ZONE = "America/Chicago";
const APPLY_ACKNOWLEDGEMENT =
  "I_APPROVE_FAILED_PAYMENT_INSTALLMENT_CONVERSION";
const DAY_MS = 24 * 60 * 60 * 1000;

interface ConversionOrder {
  id: string;
  orderNumber: string;
  customer: string;
  totalAmount: number;
}

interface InstallmentDraft {
  amount: string;
  dueDate: string;
}

interface ConversionSnapshot {
	orderId: string;
	customerId: string;
	orderPaymentId: string;
	failedScheduledPaymentId: string;
	stripeCustomerId: string;
	paymentMethodId: string;
	oldPaymentIntentId: string;
  orderNumber: string;
  customerAccountLabel: string;
  amount: number;
  failedScheduledDate: number;
}

interface ConversionRequest {
  orderId: string;
  installments: Array<{ amount: number; scheduledDate: number }>;
  customerAuthorizationConfirmed: true;
  authorizationSource: "email";
  customerAuthorizationDate: string;
}

interface ConversionPreview {
  success: true;
  request: ConversionRequest;
  snapshot: ConversionSnapshot;
  provider: {
    paymentMethodBrand: string;
    paymentMethodLast4: string;
    paymentMethodExpMonth: number;
    paymentMethodExpYear: number;
    oldPaymentIntentStatus: string;
    oldPaymentIntentAmount: number;
    relatedPaymentIntentCount: number;
  };
  plan: {
    newInstallments: Array<{
      installmentNumber: number;
      amount: number;
      scheduledDate: number;
    }>;
  };
}

interface FailedPaymentInstallmentConversionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: ConversionOrder | null;
}

function getDateParts(timestamp: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(timestamp));
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function formatDateOnly(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function dateOnlyInTimeZone(timestamp: number, timeZone: string) {
  const { year, month, day } = getDateParts(timestamp, timeZone);
  return formatDateOnly(year, month, day);
}

function addDaysToDateOnly(dateOnly: string, days: number) {
  const parts = dateOnly.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return formatDateOnly(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

export function scheduledCollectionTimestamp(dateOnly: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return Number.NaN;
  const parts = dateOnly.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return Number.NaN;
  const desiredAsUtc = Date.UTC(year, month - 1, day, 12, 0, 0);
  const date = new Date(desiredAsUtc);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) return Number.NaN;
  let candidate = desiredAsUtc;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = getDateParts(candidate, COLLECTION_TIME_ZONE);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    candidate += desiredAsUtc - actualAsUtc;
  }

  return candidate;
}

function nextThursdayDateOnly(now: number) {
  const currentDate = dateOnlyInTimeZone(now, COLLECTION_TIME_ZONE);
  const parts = currentDate.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const daysUntilThursday = ((4 - weekday + 7) % 7) || 7;
  return addDaysToDateOnly(currentDate, daysUntilThursday);
}

function amountFromCents(cents: number) {
  return (cents / 100).toFixed(2);
}

function buildInstallmentDrafts(totalAmount: number, count: number, now: number) {
  const totalCents = Math.round(totalAmount * 100);
  const regularCents = Math.floor(totalCents / count);
  const firstDate = nextThursdayDateOnly(now);

  return Array.from({ length: count }, (_, index) => ({
    amount: amountFromCents(
      index === count - 1
        ? totalCents - regularCents * (count - 1)
        : regularCents,
    ),
    dueDate: addDaysToDateOnly(firstDate, index * 7),
  }));
}

function recalculateFinalAmount(
  drafts: InstallmentDraft[],
  totalAmount: number,
) {
  if (drafts.length < 2) return drafts;
  const usedCents = drafts.slice(0, -1).reduce((sum, draft) => {
    const amount = Number(draft.amount);
    return Number.isFinite(amount) ? sum + Math.round(amount * 100) : sum;
  }, 0);
  const finalCents = Math.round(totalAmount * 100) - usedCents;
  return drafts.map((draft, index) =>
    index === drafts.length - 1
      ? { ...draft, amount: finalCents > 0 ? amountFromCents(finalCents) : "" }
      : draft,
  );
}

function parseInstallments(drafts: InstallmentDraft[]) {
  return drafts.map((draft) => ({
    amount: Number(draft.amount),
    scheduledDate: scheduledCollectionTimestamp(draft.dueDate),
  }));
}

function validateDrafts(
  drafts: InstallmentDraft[],
  totalAmount: number,
  customerAuthorizationConfirmed: boolean,
  customerAuthorizationDate: string,
  now: number,
) {
  if (!customerAuthorizationConfirmed) {
    return "Confirm that the customer authorized this schedule by email.";
  }
  if (!customerAuthorizationDate) {
    return "Enter the date of the customer's email authorization.";
  }
  const today = dateOnlyInTimeZone(now, COLLECTION_TIME_ZONE);
  if (customerAuthorizationDate > today) {
    return "The customer authorization date cannot be in the future.";
  }
  if (drafts.length < 2 || drafts.length > 12) {
    return "Choose between 2 and 12 installments.";
  }

  const installments = parseInstallments(drafts);
  let totalCents = 0;
  let previousDate = 0;
  for (const installment of installments) {
    const amountCents = Math.round(installment.amount * 100);
    if (
      !Number.isFinite(installment.amount) ||
      amountCents <= 0 ||
      Math.abs(installment.amount * 100 - amountCents) > 1e-7
    ) {
      return "Every installment must have a positive amount with no more than two decimal places.";
    }
    if (!Number.isSafeInteger(installment.scheduledDate)) {
      return "Enter a valid date for every installment.";
    }
    if (
      installment.scheduledDate <= now ||
      dateOnlyInTimeZone(installment.scheduledDate, COLLECTION_TIME_ZONE) <= today
    ) {
      return "Choose tomorrow or later so the daily collection run has not already passed.";
    }
    if (installment.scheduledDate > now + 366 * DAY_MS) {
      return "Installment dates must be within one year.";
    }
    if (installment.scheduledDate <= previousDate) {
      return "Installment dates must be in increasing order.";
    }
    previousDate = installment.scheduledDate;
    totalCents += amountCents;
  }
  if (totalCents !== Math.round(totalAmount * 100)) {
    return "Installments must add up exactly to the order total.";
  }
  return null;
}

function formatScheduledDate(timestamp: number) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: COLLECTION_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(timestamp));
}

export function FailedPaymentInstallmentConversionDialog({
  open,
  onOpenChange,
  order,
}: FailedPaymentInstallmentConversionDialogProps) {
  const previewConversion = useAction(
    (api.failedPaymentInstallmentConversion as any).previewAdminConversion,
  );
  const applyConversion = useAction(
    (api.failedPaymentInstallmentConversion as any).applyAdminConversion,
  );
  const [stage, setStage] = React.useState<"edit" | "review" | "success">(
    "edit",
  );
  const [installmentCount, setInstallmentCount] = React.useState(4);
  const [drafts, setDrafts] = React.useState<InstallmentDraft[]>([]);
  const [authorizationConfirmed, setAuthorizationConfirmed] =
    React.useState(false);
  const [authorizationDate, setAuthorizationDate] = React.useState("");
  const [cancellationConfirmed, setCancellationConfirmed] =
    React.useState(false);
  const [preview, setPreview] = React.useState<ConversionPreview | null>(null);
  const [result, setResult] = React.useState<{ status?: string } | null>(null);
  const [error, setError] = React.useState("");
  const [isWorking, setIsWorking] = React.useState(false);
  const workingRef = React.useRef(false);
  const stageHeadingRef = React.useRef<HTMLParagraphElement>(null);

  React.useEffect(() => {
    if (open && stage !== "edit") stageHeadingRef.current?.focus();
  }, [open, stage]);

  React.useEffect(() => {
    if (!open || !order) return;
    const now = Date.now();
    setStage("edit");
    setInstallmentCount(4);
    setDrafts(buildInstallmentDrafts(order.totalAmount, 4, now));
    setAuthorizationConfirmed(false);
    setAuthorizationDate(dateOnlyInTimeZone(now, COLLECTION_TIME_ZONE));
    setCancellationConfirmed(false);
    setPreview(null);
    setResult(null);
    setError("");
  }, [open, order]);

  const updateInstallmentCount = (value: string) => {
    if (!order) return;
    const count = Number(value);
    setInstallmentCount(count);
    setDrafts(buildInstallmentDrafts(order.totalAmount, count, Date.now()));
    setError("");
  };

  const updateAmount = (index: number, amount: string) => {
    if (!order) return;
    setDrafts((current) =>
      recalculateFinalAmount(
        current.map((draft, draftIndex) =>
          draftIndex === index ? { ...draft, amount } : draft,
        ),
        order.totalAmount,
      ),
    );
    setError("");
  };

  const updateDueDate = (index: number, dueDate: string) => {
    setDrafts((current) =>
      current.map((draft, draftIndex) =>
        draftIndex === index ? { ...draft, dueDate } : draft,
      ),
    );
    setError("");
  };

  const handlePreview = async () => {
    if (!order || workingRef.current) return;
    const validationError = validateDrafts(
      drafts,
      order.totalAmount,
      authorizationConfirmed,
      authorizationDate,
      Date.now(),
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    workingRef.current = true;
    setIsWorking(true);
    setError("");
    try {
      const request: ConversionRequest = {
        orderId: order.id,
        installments: parseInstallments(drafts),
        customerAuthorizationConfirmed: true,
        authorizationSource: "email",
        customerAuthorizationDate: authorizationDate,
      };
      const response = await previewConversion(request);
      if (!response?.success) {
        setError(response?.error || "This failed payment cannot be converted safely.");
        return;
      }
      setPreview({ ...response, request } as ConversionPreview);
      setCancellationConfirmed(false);
      setStage("review");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The conversion preview could not be completed.",
      );
    } finally {
      workingRef.current = false;
      setIsWorking(false);
    }
  };

  const handleApply = async () => {
    if (!order || !preview || workingRef.current) return;
    if (!cancellationConfirmed) {
      setError("Confirm that the old failed charge will be cancelled.");
      return;
    }

    workingRef.current = true;
    setIsWorking(true);
    setError("");
    try {
      const response = await applyConversion({
        ...preview.request,
        expectedSnapshot: preview.snapshot,
        cancellationAcknowledgement: APPLY_ACKNOWLEDGEMENT,
      });
      if (!response?.success) {
        setError(response?.error || "The conversion was not applied.");
        return;
      }
      setResult(response.result);
      setStage("success");
      toast.success(
        response.alreadyApplied
          ? "Installment conversion was already complete"
          : "Failed payment converted to installments",
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The failed payment could not be converted.",
      );
    } finally {
      workingRef.current = false;
      setIsWorking(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isWorking && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overscroll-contain sm:max-w-[640px]" aria-busy={isWorking}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" aria-hidden="true" />
            Convert failed payment to installments
          </DialogTitle>
          <DialogDescription>
            {order
              ? `${order.orderNumber} · ${order.customer} · ${formatCurrency(order.totalAmount)}`
              : "Create a replacement schedule for a failed card payment."}
          </DialogDescription>
        </DialogHeader>

        {stage === "edit" && order ? (
          <div className="space-y-5 py-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>
                  This workflow is only for a failed saved-card charge. It checks
                  the customer, order balance, failed attempt, and Stripe payment
                  before any changes are made.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="failed-conversion-count">Number of installments</Label>
              <Select
                value={String(installmentCount)}
                onValueChange={updateInstallmentCount}
                disabled={isWorking}
              >
                <SelectTrigger id="failed-conversion-count" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 11 }, (_, index) => index + 2).map(
                    (count) => (
                      <SelectItem key={count} value={String(count)}>
                        {count} installments
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

			<div className="space-y-3" aria-describedby="collection-time-note">
			  <div className="hidden grid-cols-2 gap-3 px-1 text-xs font-medium text-muted-foreground sm:grid">
                <span>Amount</span>
                <span>Charge date</span>
              </div>
              {drafts.map((draft, index) => {
                const isFinal = index === drafts.length - 1;
                return (
                  <div
                    key={index}
					className="grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-2"
                  >
                    <div className="space-y-1.5">
                      <Label htmlFor={`failed-installment-amount-${index}`}>
                        Installment {index + 1} amount
                      </Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          $
                        </span>
						<Input
						  id={`failed-installment-amount-${index}`}
						  className={isFinal ? "bg-muted pl-7" : "pl-7"}
						  inputMode="decimal"
						  value={draft.amount}
						  disabled={isWorking}
						  readOnly={isFinal}
						  aria-readonly={isFinal}
						  onChange={(event) => updateAmount(index, event.target.value)}
						/>
                      </div>
                      {isFinal ? (
                        <p className="text-xs text-muted-foreground">
                          Calculated remaining balance
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`failed-installment-date-${index}`}>
                        Installment {index + 1} date
                      </Label>
                      <Input
                        id={`failed-installment-date-${index}`}
                        type="date"
                        value={draft.dueDate}
                        min={addDaysToDateOnly(dateOnlyInTimeZone(Date.now(), COLLECTION_TIME_ZONE), 1)}
                        disabled={isWorking}
                        onChange={(event) =>
                          updateDueDate(index, event.target.value)
                        }
                      />
                    </div>
                  </div>
                );
              })}
              <p id="collection-time-note" className="text-xs text-muted-foreground">
                Choose tomorrow or later. Charges are collected by the daily
                payment run; the exact time may vary. The final installment is
                always the exact remaining balance.
              </p>
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div className="space-y-1.5">
                <Label htmlFor="customer-authorization-date">
                  Customer email authorization date
                </Label>
                <Input
                  id="customer-authorization-date"
                  type="date"
                  max={dateOnlyInTimeZone(Date.now(), COLLECTION_TIME_ZONE)}
                  value={authorizationDate}
                  disabled={isWorking}
                  onChange={(event) => {
                    setAuthorizationDate(event.target.value);
                    setError("");
                  }}
                  className="w-48"
                />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="customer-authorization-confirmed"
                  checked={authorizationConfirmed}
                  disabled={isWorking}
                  onCheckedChange={(checked) => {
                    setAuthorizationConfirmed(checked === true);
                    setError("");
                  }}
                />
                <Label
                  htmlFor="customer-authorization-confirmed"
                  className="font-normal leading-5"
                >
                  I verified the customer authorized these automatic card charges
                  by email.
                </Label>
              </div>
            </div>
          </div>
        ) : null}

        {stage === "review" && preview ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <div className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                <div>
                  <p ref={stageHeadingRef} tabIndex={-1} className="font-medium">Safety checks passed</p>
                  <p className="mt-1 text-muted-foreground">
                    The order, customer account, balance, failed attempt, saved card,
                    and Stripe intent all match.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border p-3 text-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Customer account
                </p>
                <p className="mt-1 font-medium">
                  {preview.snapshot.customerAccountLabel}
                </p>
              </div>
              <div className="rounded-lg border p-3 text-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Saved card
                </p>
                <p className="mt-1 flex items-center gap-2 font-medium capitalize">
                  <CreditCard className="h-4 w-4" aria-hidden="true" />
                  {preview.provider.paymentMethodBrand} ••••
                  {preview.provider.paymentMethodLast4}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Expires {String(preview.provider.paymentMethodExpMonth).padStart(2, "0")}/
                  {preview.provider.paymentMethodExpYear}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {preview.plan.newInstallments.map((installment) => (
                <div
                  key={installment.installmentNumber}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <span className="text-sm">
                      Installment {installment.installmentNumber} · {formatScheduledDate(installment.scheduledDate)}
                    </span>
                  </div>
                  <span className="font-semibold">
                    {formatCurrency(installment.amount)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t pt-2">
                <span className="font-medium">Total</span>
                <span className="font-semibold">
                  {formatCurrency(preview.snapshot.amount)}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-950 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>
                  Applying this conversion cancels the old full-balance Stripe
                  payment so it cannot be retried, then creates these automatic
                  card installments. This action does not collect a payment.
                  Recovery installments do not earn reward points.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border p-3">
              <Checkbox
                id="old-payment-cancellation-confirmed"
                checked={cancellationConfirmed}
                disabled={isWorking}
                onCheckedChange={(checked) => {
                  setCancellationConfirmed(checked === true);
                  setError("");
                }}
              />
              <Label
                htmlFor="old-payment-cancellation-confirmed"
                className="font-normal leading-5"
              >
                I understand the old full-balance payment will be cancelled and
                replaced by this installment schedule.
              </Label>
            </div>
          </div>
        ) : null}

        {stage === "success" && preview ? (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div>
                  <p ref={stageHeadingRef} tabIndex={-1} className="font-medium">
                    {result?.status === "already_applied"
                      ? "This conversion was already complete"
                      : "Installment schedule created"}
                  </p>
                  <p className="mt-1 text-sm">
                    The old full-balance payment is retired and cannot be retried.
                    {" "}
                    {preview.plan.newInstallments.length} automatic card charges
                    are now scheduled.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm text-muted-foreground">Scheduled total</span>
              <span className="font-semibold">
                {formatCurrency(preview.snapshot.amount)}
              </span>
            </div>
          </div>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}

        <DialogFooter>
          {stage === "edit" ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isWorking}
              >
                Cancel
              </Button>
              <Button onClick={handlePreview} disabled={isWorking}>
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {isWorking ? "Checking…" : "Review conversion"}
              </Button>
            </>
          ) : null}
          {stage === "review" ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStage("edit");
                  setPreview(null);
                  setCancellationConfirmed(false);
                  setError("");
                }}
                disabled={isWorking}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleApply}
                disabled={isWorking}
              >
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Split className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {isWorking
                  ? "Converting…"
                  : "Cancel old payment and create installments"}
              </Button>
            </>
          ) : null}
          {stage === "success" ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
